import { mergeClasses } from '@/unstyled/lib/utils';
import { BendBigCircle } from '../icons/BendBigCircle';
import { Text } from '../text/Text';
import './Avatar.css';

import { CSSProperties, FC, useMemo } from 'react';

export interface AvatarProps {
  name: string | null;
  url?: string | null;
  ctmType?: CareTeamType;
  appointmentType?: string;
  style?: CSSProperties;
  size?: 'large' | 'small';
  useInitials?: boolean;
  onClick?: () => void;
  className?: string;
}

export enum CareTeamType {
  BCM = '#089C9C',
  Coach = '#39BC66',
  Therapist = '#FF7C5F',
  Prescriber = '#B666B6',
  Nurse = '#6471B4',
  OS = '#F2F0EE',
  Psychometrist = '#B666B6',
  Neuropsychologist = '#B666B6',
  None = '#FFFFFF',
}

export const Avatar: FC<AvatarProps> = ({
  name,
  url,
  ctmType,
  appointmentType,
  style,
  size = 'small',
  useInitials = true,
  onClick,
  className,
}) => {
  const initials = useMemo(() => {
    if (url) return null;
    if (!useInitials) return name;
    if (!name) return '';

    return name
      .split(',')[0]
      .split(' ')
      .map((s) => s[0])
      .join('')
      .toUpperCase();
  }, [name, url, useInitials]);

  const background = useMemo(() => {
    if (ctmType) return ctmType;

    if (appointmentType) {
      switch (true) {
        case appointmentType.includes('bcm'):
          return CareTeamType.BCM;
        case appointmentType.includes('coach'):
          return CareTeamType.Coach;
        case appointmentType.includes('therap'):
          return CareTeamType.Therapist;
        case appointmentType.includes('nurse'):
          return CareTeamType.Nurse;
        case appointmentType.includes('prescriber'):
          return CareTeamType.Prescriber;
        case appointmentType.includes('psychometrist'):
          return CareTeamType.Psychometrist;
        case appointmentType.includes('neuropsychologist'):
          return CareTeamType.Neuropsychologist;
        default:
          return '#E9D4F2'; // Caregiver color
      }
    }

    return '#E9D4F2'; // Caregiver color
  }, [ctmType, appointmentType, name]);

  const color = (style?.color ?? ['#E9D4F2', CareTeamType.OS].includes(background)) ? 'black' : 'white';

  const containerStyle: CSSProperties = useMemo(() => {
    const containerStyle: CSSProperties = { backgroundColor: background, color, ...style };
    if (onClick) {
      containerStyle.cursor = 'pointer';
    }
    return containerStyle;
  }, [background, color, style]);

  const initialsVariant = () => {
    if (size === 'small') {
      return 'body-6';
    } else if (size === 'large') {
      return 'body-4';
    }

    return 'body-6';
  };

  return (
    <div className={mergeClasses([`Avatar avatar_${size}`, className ?? ''])} style={containerStyle} onClick={onClick}>
      {initials ? (
        <Text variant={initialsVariant()}>{initials}</Text>
      ) : url ? (
        <img src={url ?? undefined} alt={name ?? 'NA'} />
      ) : (
        <BendBigCircle style={{ height: 38, width: 38 }} fill={background} />
      )}
    </div>
  );
};
